import React from 'react';
import { Form, Input, Select, Table as AntTable, Tooltip } from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    InfoCircleOutlined,
    KeyOutlined,
    SafetyCertificateOutlined,
    UserOutlined,
    LockOutlined
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import SidePanel from '@/components/ui/SidePanel';
import RoleDetailPanel from './RoleDetailPanel';
import AppModal from './AppModal';
import { useRolesPage } from '@hooks/rbac/useRolesPage';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    RolesSliderTabWrap,
    RolesSearchWrapper,
    RolesSearchInput,
    CreateRoleModalBody,
    CreateRolePermSection,
    StickyPermHeader,
    CreateRolePermLabel,
    CreateRolePermToolbar,
    TabsScrollWrap,
    ThemedAntTableWrap,
    CreateRoleModalFooter,
} from './style';

const Roles = () => {
    const {
        searchText,
        setSearchText,
        modalOpen,
        setModalOpen,
        panelOpen,
        closePanel,
        openPanel,
        selectedRoleId,
        selectedRoleName,
        tableData,
        columns,
        roles,
        form,
        handleCreateRole,
        handleUpdateRole,
        permSliderTabItems,
        permModuleTab,
        setPermModuleTab,
        permSearch,
        setPermSearch,
        modalSelectedPermissionIds,
        setModalSelectedPermissionIds,
        handlePermissionSelectionChange,
        modalPermissionTableData,
        permissionsLoading,
        canView,
        canCreate,
        createRoleMutation,
        updateRoleMutation,
        closeModal,
        editingRoleId,
        setEditingRoleId,
        predefinedRoleOptions,
        selectedPredefinedRoleName,
        applyPredefinedRole,
        toggleModalPermissionId,
        isRoleChanged,
    } = useRolesPage();

    const isPredefinedSelected = !!selectedPredefinedRoleName;

    return (
        <ListBodyLayout
            title="Roles"
            description="Manage roles of your company"
            actions={
                canCreate && (
                    <Button
                        $primary
                        $blackOnLight
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingRoleId(null);
                            setModalOpen(true);
                        }}
                        $padding="10px 20px"
                        $size="14px"
                    >
                        Add Role
                    </Button>
                )
            }
            kpiCards={[
                {
                    label: 'Total Roles',
                    value: roles.length,
                    icon: <KeyOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                    trend: 'in workspace',
                    trendPositive: true,
                },
                {
                    label: 'System Roles',
                    value: roles.filter((r) => r.isSystemRole).length,
                    icon: <SafetyCertificateOutlined style={{ color: '#10b981' }} />,
                    trend: 'predefined',
                    trendPositive: true,
                },
                {
                    label: 'Custom Roles',
                    value: roles.filter((r) => !r.isSystemRole).length,
                    icon: <UserOutlined style={{ color: '#8b5cf6' }} />,
                    trend: 'user-defined',
                    trendPositive: true,
                },
                {
                    label: 'With Permissions',
                    value: roles.length,
                    icon: <LockOutlined style={{ color: '#ef4444' }} />,
                    trend: 'assign via edit',
                    trendPositive: true,
                },
            ]}
        >
            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="16px"
            >
                <Container $display="flex" $alignItems="center" $minWidth="0" />
                <Container $display="flex" $alignItems="center" $gap="10px">
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            placeholder="Search here"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
                onRow={(record) => ({
                    onClick: () => canView && openPanel(record.id),
                    style: { cursor: canView ? 'pointer' : 'default' },
                })}
            />

            <AppModal
                size="lg"
                title={editingRoleId ? 'Edit Role' : 'Create Role'}
                open={modalOpen}
                onCancel={closeModal}
                styles={{ body: { paddingTop: 8 } }}
                footer={
                    <CreateRoleModalFooter>
                        <Button $primary={false} onClick={closeModal} $padding="10px 16px" $size="14px">
                            Cancel
                        </Button>
                        <Button
                            $primary
                            $blackOnLight
                            $padding="10px 16px"
                            $size="14px"
                            loading={editingRoleId ? updateRoleMutation.isPending : createRoleMutation.isPending}
                            onClick={editingRoleId ? handleUpdateRole : handleCreateRole}
                            disabled={!!editingRoleId && !isRoleChanged}
                            lockWhenDisabled={!!editingRoleId && !isRoleChanged}
                        >
                            {editingRoleId ? 'Update Role' : 'Create Role'}
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $noMaxHeight>
                    <Form form={form} layout="vertical">
                        <Form.Item label="Predefined role (optional)">
                            <Select
                                allowClear
                                placeholder="Select a predefined role"
                                size="large"
                                options={predefinedRoleOptions}
                                value={selectedPredefinedRoleName}
                                onChange={(v) => applyPredefinedRole(v)}
                            />
                        </Form.Item>
                        <Form.Item
                            name="name"
                            label={
                                <Container $display="flex" $alignItems="center" $gap="8px">
                                    <span>Role name</span>
                                    {isPredefinedSelected && (
                                        <Tooltip title="You can’t edit the role name when a predefined role is selected. Clear the predefined role to type a custom name.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    )}
                                </Container>
                            }
                            rules={[{ required: true, message: 'Enter role name' }]}
                            extra={
                                isPredefinedSelected
                                    ? 'Role name is locked because you selected a predefined role.'
                                    : undefined
                            }
                        >
                            <Input
                                placeholder="e.g. Developer"
                                size="large"
                                disabled={isPredefinedSelected}
                            />
                        </Form.Item>

                        <CreateRolePermSection>
                            <StickyPermHeader>
                                <CreateRolePermLabel>Permissions</CreateRolePermLabel>
                                <CreateRolePermToolbar>
                                    <TabsScrollWrap>
                                        <RolesSliderTabWrap>
                                            <SliderTab
                                                items={permSliderTabItems}
                                                activeKey={permModuleTab}
                                                onChange={setPermModuleTab}
                                                fitContent
                                            />
                                        </RolesSliderTabWrap>
                                    </TabsScrollWrap>
                                    <RolesSearchWrapper>
                                        <SearchOutlined />
                                        <RolesSearchInput
                                            placeholder="Search permissions"
                                            value={permSearch}
                                            onChange={(e) => setPermSearch(e.target.value)}
                                        />
                                    </RolesSearchWrapper>
                                </CreateRolePermToolbar>
                            </StickyPermHeader>
                            <ThemedAntTableWrap>
                                <AntTable
                                    size="small"
                                    loading={permissionsLoading}
                                    dataSource={modalPermissionTableData}
                                    pagination={false}
                                    rowSelection={{
                                        selectedRowKeys: modalSelectedPermissionIds,
                                        onChange: (keys) =>
                                            handlePermissionSelectionChange(
                                                keys,
                                                modalPermissionTableData.map((r) => r.id),
                                            ),
                                    }}
                                    onRow={(record) => ({
                                        onClick: (e) => {
                                            if (e.target.closest('.ant-table-selection-column, .ant-checkbox-wrapper'))
                                                return;
                                            const id = record.id;
                                            toggleModalPermissionId(id);
                                        },
                                    })}
                                    columns={[
                                        { title: 'Module', dataIndex: 'module', key: 'module', width: 120 },
                                        { title: 'Feature', dataIndex: 'feature', key: 'feature', width: 140 },
                                        { title: 'Action', dataIndex: 'action', key: 'action', width: 120 },
                                        { title: 'Description', dataIndex: 'description', key: 'description', width: 200 },
                                    ]}
                                />
                            </ThemedAntTableWrap>
                        </CreateRolePermSection>
                    </Form>
                </CreateRoleModalBody>
            </AppModal>

            <SidePanel open={panelOpen} onClose={closePanel} title={selectedRoleName} width="480px">
                <RoleDetailPanel roleId={selectedRoleId} />
            </SidePanel>
        </ListBodyLayout>
    );
};

export default Roles;
